import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import * as distributionService from '../../services/distributionService';
import { DistributionRule, DistributionConfig } from '../../services/distributionService';
import logger from '../../utils/logger';

interface DistributionState {
  rules: DistributionRule[];
  config: DistributionConfig | null;
  loading: {
    rules: boolean;
    config: boolean;
    creating: boolean;
    updating: boolean;
    deleting: boolean;
  };
  error: string | null;
}

const initialState: DistributionState = {
  rules: [],
  config: null,
  loading: {
    rules: false,
    config: false,
    creating: false,
    updating: false,
    deleting: false,
  },
  error: null,
};

// Async Thunks
export const fetchDistributionRules = createAsyncThunk(
  'distribution/fetchRules',
  async () => {
    logger.log('Fetching distribution rules');
    const response = await distributionService.getDistributionRules();
    return response.data.data;
  }
);

export const fetchDistributionConfig = createAsyncThunk(
  'distribution/fetchConfig',
  async () => {
    logger.log('Fetching distribution config');
    const response = await distributionService.getDistributionConfig();
    return response.data.data;
  }
);

export const createRule = createAsyncThunk(
  'distribution/createRule',
  async (rule: DistributionRule) => {
    logger.log('Creating distribution rule:', rule);
    const response = await distributionService.createDistributionRule(rule);
    return response.data.data;
  }
);

export const updateRule = createAsyncThunk(
  'distribution/updateRule',
  async (rule: DistributionRule) => {
    logger.log('Updating distribution rule:', rule);
    await distributionService.updateDistributionRule(rule);
    return rule;
  }
);

export const deleteRule = createAsyncThunk(
  'distribution/deleteRule',
  async (ruleId: string) => {
    logger.log('Deleting distribution rule:', ruleId);
    await distributionService.deleteDistributionRule(ruleId);
    return ruleId;
  }
);

export const updateConfig = createAsyncThunk(
  'distribution/updateConfig',
  async (config: DistributionConfig) => {
    logger.log('Updating distribution config:', config);
    const response = await distributionService.updateDistributionConfig(config);
    return response.data.data;
  }
);

const distributionSlice = createSlice({
  name: 'distribution',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Rules
    builder.addCase(fetchDistributionRules.pending, (state) => {
      state.loading.rules = true;
      state.error = null;
    });
    builder.addCase(fetchDistributionRules.fulfilled, (state, action) => {
      state.loading.rules = false;
      state.rules = action.payload;
    });
    builder.addCase(fetchDistributionRules.rejected, (state, action) => {
      state.loading.rules = false;
      state.error = action.error.message || 'Failed to fetch rules';
    });

    // Fetch Config
    builder.addCase(fetchDistributionConfig.pending, (state) => {
      state.loading.config = true;
      state.error = null;
    });
    builder.addCase(fetchDistributionConfig.fulfilled, (state, action) => {
      state.loading.config = false;
      state.config = action.payload;
    });
    builder.addCase(fetchDistributionConfig.rejected, (state, action) => {
      state.loading.config = false;
      state.error = action.error.message || 'Failed to fetch config';
    });

    // Create Rule
    builder.addCase(createRule.pending, (state) => {
      state.loading.creating = true;
      state.error = null;
    });
    builder.addCase(createRule.fulfilled, (state, action) => {
      state.loading.creating = false;
      state.rules.push(action.payload);
    });
    builder.addCase(createRule.rejected, (state, action) => {
      state.loading.creating = false;
      state.error = action.error.message || 'Failed to create rule';
    });

    // Update Rule
    builder.addCase(updateRule.pending, (state) => {
      state.loading.updating = true;
      state.error = null;
    });
    builder.addCase(updateRule.fulfilled, (state, action) => {
      state.loading.updating = false;
      const index = state.rules.findIndex(r => r._id === action.payload._id);
      if (index !== -1) {
        state.rules[index] = action.payload;
      }
    });
    builder.addCase(updateRule.rejected, (state, action) => {
      state.loading.updating = false;
      state.error = action.error.message || 'Failed to update rule';
    });

    // Delete Rule
    builder.addCase(deleteRule.pending, (state) => {
      state.loading.deleting = true;
      state.error = null;
    });
    builder.addCase(deleteRule.fulfilled, (state, action) => {
      state.loading.deleting = false;
      state.rules = state.rules.filter(r => r._id !== action.payload);
    });
    builder.addCase(deleteRule.rejected, (state, action) => {
      state.loading.deleting = false;
      state.error = action.error.message || 'Failed to delete rule';
    });

    // Update Config
    builder.addCase(updateConfig.pending, (state) => {
      state.loading.updating = true;
      state.error = null;
    });
    builder.addCase(updateConfig.fulfilled, (state, action) => {
      state.loading.updating = false;
      state.config = action.payload;
    });
    builder.addCase(updateConfig.rejected, (state, action) => {
      state.loading.updating = false;
      state.error = action.error.message || 'Failed to update config';
    });
  },
});

export const { clearError } = distributionSlice.actions;

// Selectors
export const selectDistributionRules = (state: RootState) => state.whatsapp.distribution.rules;
export const selectDistributionConfig = (state: RootState) => state.whatsapp.distribution.config;
export const selectDistributionLoading = (state: RootState) => state.whatsapp.distribution.loading;
export const selectDistributionError = (state: RootState) => state.whatsapp.distribution.error;

export default distributionSlice.reducer;